import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { HOME } from "@/constants/testIds";

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      <input {...props} className="mt-1.5 w-full h-11 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
    </label>
  );
}

function TextArea({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      <textarea {...props} rows={4} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
    </label>
  );
}

export default function Employers() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_person: "", designation: "", phone: "", email: "", roles_hiring_for: "", number_of_openings: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries/employer", form);
      toast.success("Thanks! We'll reach out shortly.");
      setOpen(false);
      setForm({ company_name: "", contact_person: "", designation: "", phone: "", email: "", roles_hiring_for: "", number_of_openings: "", message: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <section id="employers" className="py-24 sm:py-32 bg-[#F5F7FB]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6 order-2 lg:order-1 relative">
          <div className="rounded-3xl bg-white ring-1 ring-slate-100 p-8 shadow-soft">
            <div className="text-[11px] tracking-widest uppercase text-slate-500">Hiring Partnerships</div>
            <div className="mt-3 font-display text-2xl text-[#0B132B]">Meet Better-Prepared Candidates</div>
            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              {[
                { k: "Trained", v: "Candidates" },
                { k: "Interview", v: "Ready" },
                { k: "Communication", v: "Refined" },
                { k: "Local Access", v: "Rajasthan" },
              ].map((x) => (
                <div key={x.k} className="rounded-xl p-4 ring-1 ring-slate-100">
                  <div className="text-slate-500 text-[11px] uppercase tracking-widest">{x.k}</div>
                  <div className="font-display text-lg text-[#0B132B]">{x.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="text-xs tracking-[0.28em] uppercase text-slate-500">For Employers</div>
          <h2 className="mt-3 font-display text-3xl sm:text-5xl leading-tight text-[#0B132B]">Hire from a pool of prepared talent</h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Be Hire Ready prepares candidates before they enter hiring processes by focusing on professional communication, interview readiness, resume quality, confidence and presentation.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-slate-600">
            {["Trained candidates","Interview-ready candidates","Better professional communication","Candidate preparation support","Local talent access","Hiring collaboration"].map((x) => (
              <li key={x} className="flex items-start gap-2 before:content-['·'] before:text-[#0B132B] before:mr-1">{x}</li>
            ))}
          </ul>
          <button onClick={() => setOpen(true)} data-testid={HOME.employerCta}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#0B132B] text-white text-sm font-medium hover:-translate-y-0.5 transition-transform shadow-elev">
            Partner With Be Hire Ready
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-[#0B132B]/60 backdrop-blur grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-2xl shadow-high" data-testid="employer-form">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-xs tracking-widest uppercase text-slate-500">Employer Enquiry</div>
                <div className="font-display text-2xl text-[#0B132B]">Partner with Be Hire Ready</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-500">✕</button>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <Field label="Company Name" required value={form.company_name} onChange={upd("company_name")} />
              <Field label="Contact Person" required value={form.contact_person} onChange={upd("contact_person")} />
              <Field label="Designation" value={form.designation} onChange={upd("designation")} />
              <Field label="Phone" required value={form.phone} onChange={upd("phone")} />
              <Field label="Email" type="email" required value={form.email} onChange={upd("email")} />
              <Field label="Roles Hiring For" value={form.roles_hiring_for} onChange={upd("roles_hiring_for")} />
              <Field label="Number of Openings" value={form.number_of_openings} onChange={upd("number_of_openings")} />
            </div>
            <div className="mt-4"><TextArea label="Message" value={form.message} onChange={upd("message")} /></div>
            <div className="mt-6 flex justify-end">
              <button disabled={submitting} data-testid="employer-submit"
                      className="px-6 py-3 rounded-full bg-[#0B132B] text-white text-sm font-medium disabled:opacity-60">
                {submitting ? "Sending…" : "Submit Enquiry"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
